import React, { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { Link } from "react-router-dom";
import { ShopApiError, shopAdminApi, type ShopCaseKind } from "./shopAdminApi";
import { validCaseOrderReference, validCaseOrderRevision, type CaseOrderCheck as Check } from "./caseOrderCheckModel";
import { issueLabels, stateLabels } from "./orderInspectionModel";

export default function CaseOrderCheck({ user, kind, caseId, revision, initialReference, canRead, previewOnly = false, blocked = false, onDenied, onRefreshCase }: {
  user: User | null | undefined; kind: ShopCaseKind; caseId: string; revision: number; initialReference?: string | null;
  canRead: boolean; previewOnly?: boolean; blocked?: boolean; onDenied?: () => void; onRefreshCase?: () => void;
}) {
  const scope = `${user?.uid}:${kind}:${caseId}:${revision}:${initialReference}:${canRead}:${previewOnly}:${blocked}`;
  const currentScope = useRef(scope); currentScope.current = scope;
  const [draft, setDraft] = useState({ scope: "", value: "" });
  const value = draft.scope === scope ? draft.value : validCaseOrderReference(initialReference) ? initialReference : "";
  const [result, setResult] = useState<{ scope: string; reference: string; check?: Check; error?: string; conflict?: boolean; loading?: boolean }>({ scope: "", reference: "" });
  const lock = useRef<object | null>(null), mounted = useRef(true);
  const deny = useRef(onDenied); deny.current = onDenied;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const visible = result.scope === scope && result.reference === value.trim() ? result : null;
  const loading = visible?.loading === true, allowed = !!user && canRead && !previewOnly && !blocked;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!allowed || !user || lock.current || !validCaseOrderRevision(revision)) return;
    const reference = value.trim();
    if (!validCaseOrderReference(reference)) {
      setResult({ scope, reference, error: "Ange en exakt lokal orderreferens med högst 128 bokstäver, siffror, bindestreck eller understreck." }); return;
    }
    const token = {}; lock.current = token; setResult({ scope, reference, loading: true });
    const current = () => mounted.current && currentScope.current === scope && lock.current === token;
    try {
      const check = await shopAdminApi.caseOrderCheck(user, kind, caseId, reference, revision);
      if (current()) setResult({ scope, reference, check });
    } catch (error) {
      if (current()) {
        const denied = error instanceof ShopApiError && [401, 403].includes(error.status);
        const conflict = error instanceof ShopApiError && error.status === 409;
        setResult({ scope, reference, conflict, error: denied ? "Behörigheten till ärendet eller ordern saknas eller har återkallats." :
          conflict ? "Ärendet har ändrats eller kunde inte verifieras. Hämta aktuell version innan du kontrollerar igen." :
          error instanceof ShopApiError && error.status === 404 ? "Ärendet finns inte längre." : "Orderkontrollen kunde inte genomföras. Försök igen." });
        if (denied) deny.current?.();
      }
    } finally { if (lock.current === token) lock.current = null; }
  };
  // A changed context must not keep an unrelated pending request blocking this case.
  useEffect(() => { lock.current = null; setResult({ scope: "", reference: "" }); }, [scope]);
  if (!user || !canRead || previewOnly) return null;
  const check = visible?.check, order = check?.inspection?.order;
  return <section aria-label="Kontrollera ärendets orderreferens">
    <h4>Kontrollera lokal orderreferens</h4>
    <p>Kontrollen visar om den lokala ordern och dess kopplade poster kan verifieras. Kontrollera kundens identitet och varor separat.
      En träff sparar ingen koppling och innebär ingen godkänd betalning, ångerrätt eller återbetalning.</p>
    {blocked ? <Alert severity="info">Slutför eller kontrollera den pågående ärendeåtgärden före en ny orderkontroll.</Alert> : <>
      <form aria-label="Lokal orderkontroll" onSubmit={(event) => void submit(event)}>
        <TextField label="Exakt lokal orderreferens" size="small" value={value} disabled={loading || visible?.conflict}
          onChange={(event) => { setDraft({ scope, value: event.target.value }); setResult({ scope: "", reference: "" }); }}
          inputProps={{ maxLength: 128 }} helperText="Ange orderns lokala referens. Visningsnummer och Kustom-ID söks inte." />
        <Button type="submit" disabled={loading || visible?.conflict || !validCaseOrderRevision(revision)}>Kontrollera orderreferens</Button>
      </form>
      {loading && <p role="status">Kontrollerar lokal order…</p>}
      {visible?.error && <Alert severity="error">{visible.error}{visible.conflict && <Button onClick={onRefreshCase}>Hämta aktuellt ärende</Button>}</Alert>}
      {check && <>
        <p>Kontrollerat {new Date(check.asOf).toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" })} · Ärenderevision {check.currentRevision}</p>
        {check.outcome === "not_found" && <Alert severity="info">Ingen lokal order har denna exakta referens. Ingen kund- eller nummersökning genomfördes.</Alert>}
        {check.outcome === "integrity_issue" && <Alert severity="warning">Orderns kopplade uppgifter kunde inte verifieras. Ingen orderlänk visas.
          {check.integrityIssues.map((issue) => issueLabels[issue]).join(". ")}.</Alert>}
        {order && <>
          <p>{order.account?.kustomEnvironment === "playground" ? "Playground · testorder" : "Live · produktionsorder"} · MID {order.account?.merchantId}</p>
          {order.matchesActiveAccount !== true && <Alert severity="warning">Orderns konto matchar inte ett verifierat aktivt konto. Historiken behåller sitt ursprungliga konto.</Alert>}
          <p>Lokal betalningsstatus: {stateLabels[order.paymentState]} · Reservation: {stateLabels[order.reservationState]} · Leverans: {stateLabels[order.fulfilmentState]}</p>
          <Button component={Link} to={`/webshop/orders/${order.orderId}`}>Öppna kontrollerad order</Button>
        </>}
      </>}
    </>}
  </section>;
}
